import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { SITE } from '@/app/constants';
import { usePathname } from '@/i18n/routing';
import { normalizePath } from '../../../_utils';
import { getDownloadUrl } from '../../../apis';
import type { ClawFileItem } from '../../../types';
import { ClawMarkdown } from '../../ClawMarkdown';
import { getItemName, getPreviewType, getPreviewTypeFromPath, isMarkdownFile, isTextPreviewFile } from '../utils';

export interface FilePreviewPanelHeaderActionContext {
  filePath: string;
  isTextPreview: boolean;
  textPreviewContent?: string;
  refreshTextPreview: () => void;
}

type FilePreviewPanelHeaderActions = ReactNode | ((context: FilePreviewPanelHeaderActionContext) => ReactNode);

interface FilePreviewPanelProps {
  activeFile: ClawFileItem | null;
  activeFilePath?: string;
  clawId?: string;
  onClose?: () => void;
  headerActions?: FilePreviewPanelHeaderActions;
  onResolvedPreviewUrl?: (path: string, url: string) => void;
}

const getFallbackFileName = (filePath: string): string => {
  const segments = filePath.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? filePath;
};

export const FilePreviewPanel = ({
  activeFile,
  activeFilePath,
  clawId,
  onClose,
  headerActions,
  onResolvedPreviewUrl,
}: FilePreviewPanelProps) => {
  const t = useTranslations();
  const pathname = usePathname();
  const isShareSessionPage = pathname.includes('/claw/share-session');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [textPreviewContent, setTextPreviewContent] = useState<string>();
  const [textPreviewRefreshKey, setTextPreviewRefreshKey] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [mediaSize, setMediaSize] = useState({ width: 16, height: 9 });

  const filePath = useMemo(() => normalizePath(activeFile?.path || activeFilePath), [activeFile?.path, activeFilePath]);
  const isTextPreview = useMemo(() => Boolean(filePath && isTextPreviewFile(filePath)), [filePath]);
  const previewType = useMemo(() => {
    if (activeFile) {
      return getPreviewType(activeFile);
    }
    if (filePath) {
      return getPreviewTypeFromPath(filePath);
    }
    return 'unknown';
  }, [activeFile, filePath]);
  const previewTitle = useMemo(() => {
    if (activeFile) {
      return getItemName(activeFile);
    }
    if (filePath) {
      return getFallbackFileName(filePath);
    }
    return '-';
  }, [activeFile, filePath]);
  const refreshTextPreview = useCallback(() => {
    if (!isTextPreview) {
      return;
    }

    setTextPreviewRefreshKey((refreshKey) => refreshKey + 1);
  }, [isTextPreview]);
  const renderedHeaderActions =
    typeof headerActions === 'function'
      ? headerActions({ filePath, isTextPreview, textPreviewContent, refreshTextPreview })
      : headerActions;

  const stopMediaPlayback = useCallback(
    (reason: string) => {
      void reason;
      const stopMediaElement = (mediaElement: HTMLMediaElement | null) => {
        if (!mediaElement) {
          return;
        }
        mediaElement.pause();
        mediaElement.removeAttribute('src');
        mediaElement.load();
      };
      stopMediaElement(videoRef.current);
      stopMediaElement(audioRef.current);
    },
    [filePath, pathname, previewType],
  );

  useEffect(() => {
    if (!filePath) {
      setPreviewLoading(false);
      setPreviewUrl(undefined);
      setPdfPreviewUrl(undefined);
      setTextPreviewContent(undefined);
      setErrorMessage(undefined);
      setMediaSize({ width: 16, height: 9 });
      return;
    }

    let cancelled = false;

    setPreviewLoading(true);
    setPreviewUrl(undefined);
    setPdfPreviewUrl(undefined);
    setTextPreviewContent(undefined);
    setErrorMessage(undefined);
    setMediaSize({ width: 16, height: 9 });
    getDownloadUrl({
      query: isShareSessionPage ? { file_path: filePath, claw_id: '1' } : { claw_id: clawId, file_path: filePath },
    }).then(async ({ data, error }) => {
      if (cancelled) {
        return;
      }

      if (error || !data?.download_url) {
        setPreviewLoading(false);
        if (error?.reason === 'NotFound' || error?.code === 404) {
          setErrorMessage(t('claw.file.fileDeleted'));
          return;
        }
        setErrorMessage(t('claw.file.downloadFailed'));
        return;
      }

      setPreviewUrl(data.download_url);
      onResolvedPreviewUrl?.(filePath, data.download_url);
      if (previewType === 'pdf') {
        const fileBlob = await fetch(data.download_url)
          .then((response) => {
            if (!response.ok) {
              throw new Error('fetch pdf preview failed');
            }
            return response.blob();
          })
          .catch(() => null);

        if (cancelled) {
          return;
        }

        setPreviewLoading(false);
        if (!fileBlob) {
          setErrorMessage(t('claw.file.downloadFailed'));
          return;
        }
        setPdfPreviewUrl(URL.createObjectURL(fileBlob));
        return;
      }

      if (!isTextPreview) {
        setPreviewLoading(false);
        return;
      }

      const content = await fetch(data.download_url)
        .then((response) => {
          if (!response.ok) {
            throw new Error('fetch text preview failed');
          }
          return response.text();
        })
        .catch(() => null);

      if (cancelled) {
        return;
      }

      setPreviewLoading(false);
      if (content === null) {
        setErrorMessage(t('claw.file.downloadFailed'));
        return;
      }
      setTextPreviewContent(content);
    });

    return () => {
      cancelled = true;
    };
  }, [clawId, filePath, isTextPreview, onResolvedPreviewUrl, previewType, t, textPreviewRefreshKey]);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  useEffect(() => {
    return () => {
      stopMediaPlayback('effect-cleanup:filePath-or-pathname-change-or-unmount');
    };
  }, [filePath, pathname, previewType]);

  useEffect(() => {
    const videoElement = videoRef.current;
    const audioElement = audioRef.current;

    const cleanupMedia = (mediaElement: HTMLMediaElement | null) => {
      if (!mediaElement) return;
      mediaElement.pause();
      mediaElement.removeAttribute('src');
      mediaElement.load();
    };

    return () => {
      cleanupMedia(videoElement);
      cleanupMedia(audioElement);
    };
  }, [filePath, pathname, previewType, previewUrl]);

  if (previewLoading) {
    return (
      <div className="flex size-full items-center justify-center text-system-white40">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  const renderPreview = () => {
    if (!filePath) {
      return (
        <div className="flex size-full items-center justify-center light:text-black text-sm text-system-white40">
          {t('claw.file.preview.empty')}
        </div>
      );
    }

    const binaryPreviewUrl = previewType === 'pdf' ? pdfPreviewUrl : previewUrl;

    if (!isTextPreview && !binaryPreviewUrl) {
      return (
        <div className="flex size-full items-center justify-center light:text-black text-sm text-system-white40">
          {errorMessage || t('claw.file.preview.failed')}
        </div>
      );
    }

    if (isTextPreview) {
      if (!textPreviewContent && textPreviewContent !== '') {
        return (
          <div className="flex size-full flex-col items-center justify-center gap-3 text-system-white40">
            <p className="text-sm">{errorMessage || t('claw.file.preview.unsupported')}</p>
          </div>
        );
      }

      return (
        <div className="size-full min-w-0 overflow-auto p-6">
          <ClawMarkdown content={textPreviewContent} renderAsCodeBlock={!isMarkdownFile(filePath)} />
        </div>
      );
    }

    if (previewType === 'pdf') {
      return (
        <iframe
          title={previewTitle}
          src={`${pdfPreviewUrl}#toolbar=1&navpanes=0`}
          className="size-full border-0 bg-white"
        />
      );
    }

    if (previewType === 'image') {
      const img = (
        <img
          alt={previewTitle}
          src={previewUrl}
          className="max-h-full max-w-full object-contain"
          onLoad={(e) => {
            const el = e.currentTarget;
            if (el.naturalWidth && el.naturalHeight) setMediaSize({ width: el.naturalWidth, height: el.naturalHeight });
          }}
        />
      );
      return (
        <div className="flex size-full items-center justify-center overflow-hidden">
          {SITE === 'china' ? (
            <MediaWithWatermark width={mediaSize.width} height={mediaSize.height} showWatermark>
              {img}
            </MediaWithWatermark>
          ) : (
            img
          )}
        </div>
      );
    }
    if (previewType === 'video') {
      const video = (
        <video
          ref={videoRef}
          className="max-h-full max-w-full bg-black object-contain"
          autoPlay
          loop
          playsInline
          controls
          src={previewUrl}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            if (v.videoWidth && v.videoHeight) setMediaSize({ width: v.videoWidth, height: v.videoHeight });
          }}
        >
          <track kind="captions" />
        </video>
      );
      return (
        <div className="flex size-full items-center justify-center overflow-hidden">
          {SITE === 'china' ? (
            <MediaWithWatermark width={mediaSize.width} height={mediaSize.height} showWatermark>
              {video}
            </MediaWithWatermark>
          ) : (
            video
          )}
        </div>
      );
    }
    if (previewType === 'audio') {
      return (
        <div className="flex size-full items-center justify-center px-8">
          <audio ref={audioRef} className="w-full" controls src={previewUrl}>
            <track kind="captions" />
          </audio>
        </div>
      );
    }
    return (
      <div className="flex size-full flex-col items-center justify-center gap-3 text-system-white40">
        <p className="text-sm">{errorMessage || t('claw.file.preview.unsupported')}</p>
      </div>
    );
  };

  return (
    <div className="flex size-full flex-col overflow-hidden rounded-12 bg-black light:bg-[rgba(0,0,0,0.08)] light:text-black">
      <div className="flex items-center justify-between border-system-white08 border-b px-4 py-2">
        <div className="truncate light:text-black text-system-white80 text-xs">{previewTitle}</div>
        <div className="ml-3 flex shrink-0 items-center gap-1">
          {renderedHeaderActions}
          {onClose ? (
            <button
              type="button"
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-8 light:text-white/64 text-system-white64 transition-colors hover:bg-system-white08 light:hover:bg-white/8 hover:text-system-white light:hover:text-white"
              onClick={onClose}
              aria-label="Close preview"
            >
              <BaseLineClose className="size-3.5" />
            </button>
          ) : null}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden" style={{ height: 0 }}>
        {renderPreview()}
      </div>
    </div>
  );
};
